// Global Instructions Rule Applied!
//
// The Pools grid of the fund Terms tab's Waterfall section (waterfall phase 6
// PR 3): the pools of a waterfall that has them - a pool is a calendar year,
// its investments the ones bought in that year, its capital the capital
// contributed in it - with their figures as at today.
//
// Read-only figures, all from the server (wf__waterfall_detail, which reads
// wf__pool_results - the one source of every pool figure): nothing is
// computed here. A row opens to the pool's investments; while no distribution
// is booked an investment can be moved to another pool. What falls in no pool
// - capital contributed in a year without one, a realization (a write-off
// is one) on an investment assigned to none - is named above the grid, because the
// engine refuses to compute a distribution until it has a pool.

import React from 'react'
import { Alert, Select, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import NumberText from '../../../../../core/components/NumberText.tsx'
import type {
  WaterfallPool,
  WaterfallPoolGap,
  WaterfallPoolTranche
} from '../../../../../state_manager/data_models/fund_waterfall_record.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const dash = <Text type='secondary'>—</Text>
const money = (v: number | null | undefined): React.ReactNode => <NumberText value={v} />
const date = (v: string | null | undefined): React.ReactNode => (v ? dayjs(v).format(DATE_FORMAT) : dash)

// the gaps that stop a distribution from being computed (an investment that
// is merely unassigned does not)
const BLOCKING_GAPS = new Set(['contribution', 'realization', 'journal'])
const GAP_WORDS: Record<string, string> = {
  contribution: 'Capital contributed',
  realization: 'Realization',
  journal: 'Investment journal without a tranche',
  investment: 'Investment'
}

interface WaterfallPoolsGridProps {
  pools: WaterfallPool[]
  gaps: WaterfallPoolGap[]
  /** the date the figures are as at (ISO) */
  asOf: string
  /** a booked distribution locks the investments in their pools */
  locked: boolean
  /** the investment being moved, while its save runs */
  movingTrancheId: string | null
  onMove: (trancheId: string, poolId: string) => void
}

export default function WaterfallPoolsGrid({ pools, gaps, asOf, locked, movingTrancheId, onMove }: WaterfallPoolsGridProps) {
  const poolOptions = pools.map((p) => ({ value: p.id, label: p.pool_code }))
  const blocking = gaps.filter((g) => BLOCKING_GAPS.has(g.kind))
  const unassigned = gaps.filter((g) => !BLOCKING_GAPS.has(g.kind))

  const columns: ColumnsType<WaterfallPool> = [
    { title: 'Pool', dataIndex: 'pool_code', width: 90, fixed: 'left' },
    { title: 'Investments', dataIndex: 'investments', width: 100, align: 'right' },
    { title: 'Cost', dataIndex: 'cost', width: 130, align: 'right', render: money },
    {
      title: 'Cost Held',
      key: 'cost_held',
      width: 130,
      align: 'right',
      render: (_: unknown, p) => money(p.cost - p.cost_divested)
    },
    { title: 'Proceeds', dataIndex: 'proceeds', width: 130, align: 'right', render: money },
    { title: 'Realized Gain', dataIndex: 'net_realized', width: 130, align: 'right', render: money },
    { title: 'Contributed', dataIndex: 'contributions', width: 130, align: 'right', render: money },
    { title: 'Return of capital', dataIndex: 'capital_returned', width: 140, align: 'right', render: money },
    { title: 'Loss offset received', dataIndex: 'remediation_received', width: 150, align: 'right', render: money },
    { title: 'Loss offset paid', dataIndex: 'remediation_given', width: 140, align: 'right', render: money },
    { title: 'Unreturned capital', dataIndex: 'capital_due', width: 150, align: 'right', render: money },
    { title: 'Carry paid', dataIndex: 'carry_paid', width: 120, align: 'right', render: money },
    { title: 'Loss to offset', dataIndex: 'unremedied_loss', width: 130, align: 'right', render: money }
  ]

  const trancheColumns = (pool: WaterfallPool): ColumnsType<WaterfallPoolTranche> => [
    { title: 'Company', dataIndex: 'company_name', render: (v: string | null | undefined) => v ?? dash },
    { title: 'Tranche', dataIndex: 'tranche_name', render: (v: string | null | undefined) => v ?? dash },
    { title: 'Bought', dataIndex: 'bought', width: 110, render: date },
    { title: 'Cost', dataIndex: 'cost', width: 130, align: 'right', render: money },
    {
      title: 'Cost Held',
      key: 'cost_held',
      width: 130,
      align: 'right',
      render: (_: unknown, t) => money(t.cost - t.cost_divested)
    },
    { title: 'Proceeds', dataIndex: 'proceeds', width: 130, align: 'right', render: money },
    {
      title: 'Pool',
      key: 'move',
      width: 130,
      render: (_: unknown, t) => {
        const select = (
          <Select
            size='small'
            className='w-full'
            value={pool.id}
            options={poolOptions}
            disabled={locked}
            loading={movingTrancheId === t.tranche_id}
            aria-label={`Pool of ${t.tranche_name ?? 'this investment'}`}
            onChange={(poolId) => {
              if (poolId !== pool.id) onMove(t.tranche_id, poolId)
            }}
          />
        )
        return locked ? (
          <Tooltip title='The fund has a booked distribution: the pool of a source decided whose capital it returned. Revert the distributions to move an investment.'>
            <span className='block'>{select}</span>
          </Tooltip>
        ) : (
          select
        )
      }
    }
  ]

  return (
    <div className='mt-4' data-testid='waterfall-pools'>
      <Text strong>Pools</Text>
      {blocking.length > 0 ? (
        <Alert
          className='mt-1 mb-2'
          type='warning'
          showIcon
          message='Something falls in no pool - a distribution cannot be computed until it has one'
          description={
            <div>
              <ul className='list-disc pl-5 mb-1'>
                {blocking.slice(0, 5).map((g, i) => (
                  <li key={`${g.kind}-${g.ref_id ?? g.gap_date ?? i}`}>
                    {GAP_WORDS[g.kind] ?? g.kind}
                    {g.label && g.kind !== 'contribution' ? `: ${g.label}` : ''}
                    {g.gap_date ? ` (${dayjs(g.gap_date).format(DATE_FORMAT)})` : ''}
                    {g.amount != null ? (
                      <>
                        {' - '}
                        <NumberText value={g.amount} />
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
              {blocking.length > 5 ? <Text type='secondary'>and {blocking.length - 5} more. </Text> : null}
              <Text type='secondary'>Create year pools adds the missing year and assigns its investments.</Text>
            </div>
          }
        />
      ) : null}
      {unassigned.length > 0 ? (
        <Alert
          className='mt-1 mb-2'
          type='info'
          showIcon
          message={`${unassigned.length} investment${unassigned.length === 1 ? ' is' : 's are'} in no pool yet`}
          description='Create year pools assigns each investment to the pool of the year it was bought.'
        />
      ) : null}
      <Table<WaterfallPool>
        size='small'
        rowKey='id'
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={pools}
        className='mt-1'
        expandable={{
          rowExpandable: (p) => p.tranches.length > 0,
          expandedRowRender: (p) => (
            <div className='py-1'>
              <Table<WaterfallPoolTranche>
                size='small'
                rowKey='tranche_id'
                pagination={false}
                columns={trancheColumns(p)}
                dataSource={p.tranches}
              />
            </div>
          )
        }}
      />
      <Text type='secondary' className='block mt-1'>
        Figures as at {dayjs(asOf).format(DATE_FORMAT)}. A pool is a calendar year: its investments are the ones bought
        in that year (a conversion or a split stays with the original purchase), its capital the capital contributed
        in it. The pool returns that capital first; unreturned capital is what is left of it. Loss to offset is a
        realized loss - a write-off, or a sale below cost - that the pool&apos;s own gains have not covered and no
        other pool has offset yet.
      </Text>
    </div>
  )
}
